<script setup lang="ts">
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import InteractionTypeIcon from '@/js/components/interactions/InteractionTypeIcon.vue';
    import RadioOption from '@/js/components/RadioOption.vue';
    import { IInteraction } from '@/js/services/api/interaction/types';
    import { CoachingType, IProtocol } from '@/js/services/api/protocol/protocolModel';
    import { useMutationPostApproveProtocols } from '@/js/services/api/protocol/protocolQueries';
    import { useCoachInteractionStore } from '@/js/stores/coach-interaction';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { channelNameTranslations } from '@/js/services/api/interaction/consts';
    import { dateToString } from '@/js/utils/dateUtils';
    import { computed } from 'vue';
    import { CheckIcon, HourglassIcon } from '@lucide/vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';

    const protocol = defineModel<IProtocol>('protocol', { required: true });

    const props = defineProps<{
        interaction?: IInteraction;
        editable?: boolean;
    }>();

    const coachInteractionStore = useCoachInteractionStore();

    const { mutateAsync: approveProtocol } = useMutationPostApproveProtocols([protocol.value.id]);

    const { hasPermission } = usePermissions();

    const canApproveProtocols = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ApproveProtocols
    );

    const coachingTypeModel = computed<CoachingType>({
        get: () =>
            props.editable
                ? coachInteractionStore.protocol.coachingType
                : protocol.value.coachingType,
        set: (value) => {
            if (props.editable) {
                coachInteractionStore.protocol.coachingType = value;
            }
        },
    });

    const updatedAt = computed(() => {
        return dateToString(new Date(protocol.value.updatedAt));
    });

    const createdAt = computed(() => {
        return dateToString(new Date(protocol.value.createdAt));
    });
</script>
<template>
    <div class="rounded-2xl p-5 bg-white border border-gray-light flex flex-col gap-10">
        <div class="flex justify-between items-start">
            <div class="flex gap-5 flex-start">
                <InteractionTypeIcon
                    v-if="interaction"
                    :gray-scale="protocol.requiresApproval && protocol.approved !== true"
                    :channel-icon="protocol.channelIcon"
                />
                <dl class="flex gap-10">
                    <div>
                        <dt class="text-overline mb-1">
                            {{ __('agent') }}
                        </dt>
                        <dd class="text-body">
                            {{ protocol.agentName }}
                        </dd>
                    </div>
                    <div v-if="interaction || protocol.manualInteractionReference">
                        <dt class="text-overline mb-1">
                            {{ __('interaction-id') }}
                        </dt>
                        <dd class="text-body">
                            {{
                                interaction
                                    ? interaction.externalId
                                    : protocol.manualInteractionReference
                            }}
                        </dd>
                    </div>
                    <div v-if="interaction">
                        <dt class="text-overline mb-1">
                            {{ __('interaction-media-type') }}
                        </dt>
                        <dd class="text-body">
                            {{
                                channelNameTranslations[interaction.channelName]
                                    ? __untypedExpression(
                                          channelNameTranslations[interaction.channelName]
                                      )
                                    : interaction.channelName
                            }}
                        </dd>
                    </div>
                </dl>
                <div class="flex gap-2 items-start ml-5">
                    <RadioOption
                        :id="'sideBySide-' + protocol.id"
                        v-model="coachingTypeModel"
                        :name="'coachingType-' + protocol.id"
                        :label="__('side-by-side')"
                        :value="CoachingType.SIDE_BY_SIDE"
                        :disabled="!editable"
                    />
                    <RadioOption
                        :id="'remote-' + protocol.id"
                        v-model="coachingTypeModel"
                        :name="'coachingType-' + protocol.id"
                        :label="__('remote')"
                        :value="CoachingType.REMOTE"
                        :disabled="!editable"
                    />
                </div>
            </div>
            <div v-if="protocol.requiresApproval">
                <EchoTooltip
                    v-if="(canApproveProtocols || protocol.isSelfCoached) && !protocol.approved"
                    :label="__('protocol-synced-from-v1-tooltip')"
                    :disabled="!protocol.isSyncedFromV1"
                    :tabindex="protocol.isSyncedFromV1 ? 0 : undefined"
                >
                    <EchoButton
                        variant="primary"
                        :disabled="protocol.isSyncedFromV1"
                        @click="approveProtocol()"
                    >
                        {{ __('protocol-approve') }}
                        <template #icon-right><HourglassIcon /></template>
                    </EchoButton>
                </EchoTooltip>
                <div v-else-if="protocol.approved" class="text-body-bold flex items-center gap-1">
                    {{ __('protocol-approved') }}
                    <CheckIcon class="size-5" />
                </div>
                <div v-else class="text-body-bold flex items-center gap-1">
                    {{ __('protocol-pending-approval') }}
                    <HourglassIcon class="size-5" />
                </div>
            </div>
        </div>
        <div class="flex justify-between">
            <p class="text-body">
                {{
                    __('protocol-detail-created-by', {
                        name: protocol.submittedBy,
                        date: createdAt,
                    })
                }}
            </p>
            <p class="text-body text-right">
                {{
                    __('protocol-detail-updated-at', {
                        date: updatedAt,
                    })
                }}
            </p>
        </div>
    </div>
</template>
